import React, { useState, useRef } from 'react';
import { Upload, X, Image, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

export const PhotoUploader = ({ listingId, photos, onPhotosChange, maxPhotos = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = maxPhotos - photos.length;
    if (files.length > remainingSlots) {
      toast.error(`You can only add ${remainingSlots} more photo${remainingSlots !== 1 ? 's' : ''}`);
      return;
    }

    setUploading(true);

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }

      try {
        // Convert to base64
        const base64 = await fileToBase64(file);
        
        if (listingId) {
          // If we have a listing ID, upload to backend
          const headers = getAuthHeaders();
          const res = await axios.post(`${API}/photos/upload`, {
            listing_id: listingId,
            photo_data: base64
          }, { withCredentials: true, headers });
          
          onPhotosChange([...photos, res.data.url]);
          toast.success('Photo uploaded!');
        } else {
          // Store locally as base64 for new listings
          onPhotosChange([...photos, base64]);
          toast.success('Photo added!');
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRemove = async (index) => {
    const photoUrl = photos[index];
    
    // If it's a backend photo URL, delete it
    if (listingId && photoUrl.startsWith('/api/photos/')) {
      const photoId = photoUrl.replace('/api/photos/', '');
      try {
        const headers = getAuthHeaders();
        await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true, headers });
        toast.success('Photo removed');
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Failed to remove photo');
        return;
      }
    }

    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  };

  const getPhotoSrc = (photo) => {
    if (photo.startsWith('data:')) {
      return photo; // Base64
    }
    if (photo.startsWith('/api/')) {
      return `${API.replace('/api', '')}${photo}`; // Backend URL
    }
    return photo; // External URL
  };

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="photo-file-input"
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {photos.map((photo, i) => (
          <div 
            key={i} 
            className="relative aspect-video rounded-xl overflow-hidden bg-slate-100 group"
            data-testid={`photo-preview-${i}`}
          >
            <img 
              src={getPhotoSrc(photo)} 
              alt={`Photo ${i + 1}`} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1571799923032-df849d54bcb9?w=400';
              }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="absolute top-2 right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
              data-testid={`remove-photo-${i}`}
            >
              <X className="w-4 h-4 text-red-500" />
            </button>
            {i === 0 && (
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded-md">
                Cover
              </span>
            )}
          </div>
        ))}

        {photos.length < maxPhotos && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-video rounded-xl border-2 border-dashed border-slate-300 hover:border-[#5ebc67] flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-[#5ebc67] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-photo-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 animate-spin" />
                <span className="text-sm">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8" />
                <span className="text-sm">Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>

      <p className="text-sm text-slate-500">
        Add up to {maxPhotos} photos. First photo will be the cover image. Max 5MB each.
      </p>
    </div>
  );
};

export default PhotoUploader;
